import { Segment } from '@dna/analytics'
import {
  getCategoryFromTags,
  minorToMajorUnit,
  splitProductName,
} from '@dna/utils'

// Initializes the wishlist functions and adds event listeners
const init = (_swat) => {
  fetchList(_swat)
  if (!window.swymSelectedListId) {
    createList(_swat)
  }
  addSwymEventListener(_swat)
}

// Creates a new wishlist if it doesn't already exist
const createList = (_swat) => {
  const listConfig = { lname: 'My Wishlist' }

  _swat.createList(
    listConfig,
    ({ lid }) => {
      console.log('Successfully created a new List', lid)
      window.swymSelectedListId = lid
    },
    (error) => console.error('Error while creating a List', error),
  )
}

// Fetches all wishlist data
const fetchList = (_swat) => {
  _swat.fetchLists({
    callbackFn: (lists) => {
      console.log('Fetched all Lists', lists)
      window.swymWishLists = lists
      window.swymSelectedListId = lists?.[0]?.lid || null
      updateButtonState()
    },
    errorFn: (error) => console.error('Error fetching all Lists', error),
  })
}

// Refreshes the wishlist by fetching the list again
const refreshList = (_swat) => {
  window.swymWishLists = null
  fetchList(_swat)
}

// Adds a product to the wishlist
const addToWishlist = (_swat, product, event) => {
  const button = event.currentTarget.closest('swymcs-wishlist-button')
  const innerButton = button.querySelector('#swymcs-wishlist-button')
  const lid = WishlistPlusListUIContext?.WishlistListDefaultList.lid

  _swat.addToList(
    lid,
    product,
    (addedItem) => {
      console.log('Product added to wishlist!', addedItem)
      _swat.initializeActionButtons('body')
    },
    (error) => {
      console.error('Error adding product to wishlist', error)
      _swat.initializeActionButtons('body')
    },
  )

  // @segment
  Segment.protect(() => {
    const item_price = innerButton.dataset.price
    const item_product_category = innerButton.dataset.productCategory
    const item_title = innerButton.dataset.title
    const wishlistHeartContext = button.closest('.js-docked-controls')
      ? 'pdp docked controls'
      : button.closest('.js-product-details')
        ? 'pdp'
        : button.closest('.js-product-card-link')
          ? 'product card'
          : button.closest('.js-quick-view')
            ? 'quick view'
            : undefined

    Segment.track(Segment.CONSTANTS.EVENTS.WISHLIST_HEART_CLICKED, {
      category: splitProductName(item_title).style,
      name: splitProductName(item_title).color,
      price: Number(minorToMajorUnit(item_price)),
      product_category: item_product_category,
      product_id: product.empi,
      wishlist_heart_context: wishlistHeartContext,
      wishlist_heart_state: 'selected',
    })
  })
}

// Removes a product from the wishlist
const removeFromWishlist = (_swat, product, event) => {
  const button = event.currentTarget.closest('swymcs-wishlist-button')
  const innerButton = button.querySelector('#swymcs-wishlist-button')
  const lid = WishlistPlusListUIContext?.WishlistListDefaultList.lid

  _swat.deleteFromList(
    lid,
    product,
    (deletedItem) => {
      console.log('Product removed from wishlist!', deletedItem)
      _swat.initializeActionButtons('body')
    },
    (error) => {
      console.error('Error removing product from wishlist', error)
      _swat.initializeActionButtons('body')
    },
  )

  // @segment
  Segment.protect(() => {
    const item_price = innerButton.dataset.price
    const item_product_category = innerButton.dataset.productCategory
    const item_title = innerButton.dataset.title
    const wishlistHeartContext = button.closest('.js-docked-controls')
      ? 'pdp docked controls'
      : button.closest('.js-product-details')
        ? 'pdp'
        : button.closest('.js-product-card-link')
          ? 'product card'
          : button.closest('.js-quick-view')
            ? 'quick view'
            : undefined

    Segment.track(Segment.CONSTANTS.EVENTS.WISHLIST_HEART_CLICKED, {
      category: splitProductName(item_title).style,
      name: splitProductName(item_title).color,
      price: Number(minorToMajorUnit(item_price)),
      product_category: item_product_category,
      product_id: product.empi,
      wishlist_heart_context: wishlistHeartContext,
      wishlist_heart_state: 'deselected',
    })
  })
}

// Updates the state of wishlist buttons based on wishlist items.
const updateButtonState = () => {
  const buttons = document.querySelectorAll('.swymcs-wishlist-button')

  for (const button of buttons) {
    const { epi, empi } = button.dataset
    let isWishlisted = false

    if (WishlistPlusListUIContext.WishlistListDefaultList) {
      for (const item of WishlistPlusListUIContext.WishlistListDefaultList
        ?.listcontents ?? []) {
        if (
          Number(item.empi) === Number(empi) &&
          Number(item.epi) === Number(epi)
        ) {
          isWishlisted = true
          break
        }
        if (isWishlisted) break
      }
    }

    updateButtonWishlistState(button, isWishlisted)
  }
}

// Updates button visuals based on wishlist state
const updateButtonWishlistState = (button, isWishlisted) => {
  const settings = button.dataset.settings
    ? JSON.parse(button.dataset.settings)
    : {}

  button.classList.toggle('swym-added-custom', isWishlisted)

  if (isWishlisted && settings.disable_added_to_wishlist) {
    button.disabled = true
  } else {
    button.disabled = false
  }
}

// Handles wishlist-related events
const addSwymEventListener = (_swat) => {
  _swat.evtLayer.addEventListener(_swat.JSEvents.addedToWishlist, (event) => {
    const { iu: image, dt: title } = event.detail.d
    showCustomNotification(_swat, image, title, 'add', 1)
    refreshList(_swat)
  })

  _swat.evtLayer.addEventListener(
    _swat.JSEvents.removedFromWishlist,
    (event) => {
      const { iu: image, dt: title } = event.detail.d
      showCustomNotification(_swat, image, title, 'remove', 1)
      refreshList(_swat)
    },
  )
}

// Displays a custom notification for wishlist actions
const showCustomNotification = (_swat, image, title, action, listCount) => {
  const message = `
    <div id="swym-custom-notification-content" onclick="__swat.ui.open()">
      <div id="swym-custom-image-container">
        <img id="swym-custom-image" src="${image}" alt="Product image" />
      </div>
      <div id="swym-custom-title-container">
        <strong>${title}</strong> has been ${
          action === 'remove' ? 'removed from' : 'added to'
        } <strong>${listCount === 1 ? 'the list!' : 'lists!'}</strong>
      </div>
    </div>
  `
  // Replace with actual notification logic
  //console.log(message)
}

// Initializes custom `swymcs-wishlist-button` element
class SwymWishlistButton extends HTMLElement {
  constructor() {
    super()
    this.isWishlisted = false
    this.initElement()
    this.checkButtonState()
    this.hideButton()
    if (!window.SwymCallbacks) {
      window.SwymCallbacks = []
    }
    window.SwymCallbacks.push(this.initializeSwymButton.bind(this))
  }

  connectedCallback() {
    this.svg = this.querySelector('svg')
  }

  initElement() {
    this.button = this.querySelector('button')
  }

  updateWishlistButtonState() {
    const relatedButton = this.button
    if (this.isWishlisted) {
      this.button.classList.add('swym-added-custom')
      this.svg.setAttribute('fill', '#03143B')
    } else {
      this.button.classList.remove('swym-added-custom')
      this.svg.setAttribute('fill', 'none')
    }
  }

  checkButtonState() {
    document.addEventListener(
      WishlistPlusListUIContext.CustomEvents?.SwymWishlistFetched,
      (event) => {
        const defaultList = event.detail.defaultList

        for (const product of defaultList?.listcontents ?? []) {
          if (
            Number(product.empi) === Number(this.button.dataset.empi) &&
            Number(product.epi) === Number(this.button.dataset.epi)
          ) {
            this.isWishlisted = true
            break
          }
        }

        this.updateWishlistButtonState()
      },
    )
  }

  updateCurrentButtonState() {
    this.isWishlisted = false
    const defaultList = WishlistPlusListUIContext.WishlistListDefaultList
    for (const product of defaultList?.listcontents ?? []) {
      if (
        Number(product.empi) === Number(this.button.dataset.empi) &&
        Number(product.epi) === Number(this.button.dataset.epi)
      ) {
        this.isWishlisted = true
        break
      }
    }

    this.updateWishlistButtonState()
  }

  initializeSwymButton(_swat) {
    const button = this.querySelector('button')
    this.showButton()

    button.addEventListener('click', (event) => {
      const relatedButton = event.target.closest('swymcs-wishlist-button')
      const relatedInnerButton = relatedButton.querySelector('button')
      const product = {
        epi: relatedInnerButton.dataset.epi,
        empi: relatedInnerButton.dataset.empi,
        du: relatedInnerButton.dataset.du,
      }
      if (_swat.isCollectionsEnabled()) {
        // Example function for handling multiple collections
        renderPopup(product)
      } else {
        if (relatedButton.isWishlisted) {
          this.isWishlisted = false
          this.updateWishlistButtonState()
          removeFromWishlist(_swat, product, event)
        } else {
          this.isWishlisted = true
          this.updateWishlistButtonState()
          addToWishlist(_swat, product, event)
        }
      }
    })
  }

  showButton() {
    this.button.style.display = 'block'
  }

  hideButton() {
    this.button.style.display = 'none'
  }
}

// Register the custom element
if (!customElements.get('swymcs-wishlist-button')) {
  customElements.define('swymcs-wishlist-button', SwymWishlistButton)
}

// Global object to expose functions
const swymcsWishlistFunctions = {
  init,
  fetchList,
  refreshList,
  addToWishlist,
  removeFromWishlist,
  updateButtonState,
}

// Attach `init` to SwymCallbacks
if (!window.SwymCallbacks) {
  window.SwymCallbacks = []
}
window.SwymCallbacks.push(swymcsWishlistFunctions.init)

// Expose functions globally for debugging
window.swymcsWishlistFunctions = swymcsWishlistFunctions

const updateWishlistButtonData = (wishlistButtonContainer, detail) => {
  if (wishlistButtonContainer) {
    const pdpWishlistButton = wishlistButtonContainer.querySelector('button')
    if (pdpWishlistButton) {
      pdpWishlistButton.dataset.empi = detail.id
      pdpWishlistButton.dataset.epi = detail.swymVariantId
      if (location.pathname.includes('/products')) {
        pdpWishlistButton.dataset.du = location.href.split('?')[0]
      }
      pdpWishlistButton.dataset.price = detail.priceRegular
      pdpWishlistButton.dataset.productCategory = getCategoryFromTags(
        detail.tags,
      )
      pdpWishlistButton.dataset.title = detail.title
      wishlistButtonContainer.updateCurrentButtonState()
    }
  }
}

const onSwymHandleSwatchOptionChange = ({ detail }) => {
  const customWishlistButton =
    detail.context === 'plp' && detail.buttonElement // @intelligems TECH-1516
      ? detail.buttonElement
      : document.querySelector('#swymcs-wishlist-pdp-button')
  const customWishlistDockerButton = document
    .querySelector('dna-product-docked-controls')
    ?.shadowRoot.querySelector('#swymcs-wishlist-pdp-docker-button')

  if (customWishlistButton) {
    updateWishlistButtonData(customWishlistButton, detail)
  }
  if (customWishlistDockerButton) {
    updateWishlistButtonData(customWishlistDockerButton, detail)
  }
}

document.addEventListener(
  'dna:pdp:option-change',
  onSwymHandleSwatchOptionChange,
)

// @intelligems TECH-1516
document.addEventListener(
  'dna:plp:option-change',
  onSwymHandleSwatchOptionChange,
)
// END / TECH-1516
